import { gql } from "@urql/core";
import { useMutation } from "@urql/vue";
import { useI18n } from "vue-i18n";
import type { ManualSharingChoice } from "./useManualSharingConsent";

type Target = {
  documentSpaceId: string;
  expectedCrmContactId: string | null;
  documentSpaceItemId?: string | null;
  campaignPhaseLinkId?: string | null;
};
type ConfirmationInput = Target & {
  attemptId: string;
  client: "web_classic";
  action: "copy";
  declarationCode: string;
  declarationHeading: string;
  declarationStatement: string;
  locale: string;
  textVersion: string;
};
type ConfirmationResult = {
  confirmManualSharing?: {
    result?: { eventId?: string | null; confirmedAt?: string | null } | null;
  } | null;
};
const mutation = gql`
  mutation ConfirmManualSharing($input: ConfirmManualSharingInput!) {
    confirmManualSharing(input: $input) {
      result {
        eventId
        confirmedAt
      }
    }
  }
`;

export const useManualSharingConfirmation = () => {
  const { t, locale } = useI18n();
  const { executeMutation } = useMutation<
    ConfirmationResult,
    { input: ConfirmationInput }
  >(mutation);
  const forTarget = (target: Target) => {
    let input: ConfirmationInput | undefined;
    return async (
      attemptId: string,
      choice: ManualSharingChoice,
      signal: AbortSignal,
    ) => {
      // Freeze exactly the declaration shown at explicit Continue. Technical
      // retries reuse this payload even when the first response was lost.
      input ??= {
        ...target,
        attemptId,
        client: "web_classic",
        action: "copy",
        declarationCode:
          choice === "veeva" ? "veeva_approved_email" : "one_time_outlook",
        declarationHeading: t(`documentSpace.manualConsent.${choice}Title`),
        declarationStatement: t(
          `documentSpace.manualConsent.${choice}Declaration`,
        ),
        locale: locale.value,
        textVersion: "2026-09-16",
      };
      signal.throwIfAborted();
      // Preserve the shared client's dynamic authentication options. A save may
      // commit after cancellation; the guards still prevent stale output.
      const response = await executeMutation({ input });
      signal.throwIfAborted();
      const result = response.data?.confirmManualSharing?.result;
      if (response.error || !result?.eventId || !result.confirmedAt)
        throw (
          response.error ??
          new Error("MANUAL_CONFIRMATION_ACKNOWLEDGEMENT_MISSING")
        );
    };
  };
  return { forTarget, locale };
};
